"use client"

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Receipt, Loader2, X, AlertTriangle, Info, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "../../ui/button"
import { gateFeeApi } from "../../../services/gateFeeApi"
import type { GateFeeCandidate } from "../../../services/gateFeeApi"

/**
 * Batch gate-fee payment.
 *
 * The operator clicks the receipt icon on a row in the task list; this lists every
 * container that still owes a gate fee and sends the ticked ones into one eModal
 * session.
 *
 * Unlike batch booking there is no terminal grouping: the fee search takes
 * container numbers and nothing else, and one cart can hold fees from several
 * terminals (a real receipt lists them per terminal). The one hard constraint is
 * direction — eModal's search is IMPORT/EXPORT single-choice, and asking the wrong
 * one comes back as an empty table, which reads exactly like "nothing is owed".
 * That is the worst way for this to fail, so mixing is blocked up front.
 *
 * Containers we have already paid are listed but not selectable, with the reason
 * on the row. Hiding them makes an operator think the container vanished and go
 * pay it again by hand — which is the duplicate payment we are here to prevent.
 */

type SortKey = 'terminal' | 'client' | 'trade'

/**
 * 可排序表头。
 *
 * **必须定义在组件外面。** 写在组件体内的话，每次重渲染都会产生一个新的组件类型，
 * React 于是把整片表头卸载重建 —— DOM 节点被换掉，上一次拿到的引用就脱离文档了，
 * 连点两下排序第二下会打在空气上。（这条是测试逼出来的：连点两次方向不翻转。）
 */
const SortHeader: React.FC<{
  label: string
  k: SortKey
  sort: { key: SortKey; dir: 'asc' | 'desc' } | null
  onToggle: (k: SortKey) => void
}> = ({ label, k, sort, onToggle }) => (
  <th className="px-2 py-2 text-left select-none">
    <button type="button" onClick={() => onToggle(k)}
      className="inline-flex items-center gap-1 hover:text-gray-700"
      aria-label={`Sort by ${label}`}>
      {label}
      {sort?.key === k && (sort.dir === 'asc'
        ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
    </button>
  </th>
)

interface GateFeeBatchModalProps {
  open: boolean
  onClose: () => void
  /** The row the operator clicked. The list starts from it. */
  anchorTaskId: number | null
  /** Ticked and confirmed. Returning false leaves the modal open. */
  onPay: (taskIds: number[]) => Promise<boolean>
}

export function GateFeeBatchModal({ open, onClose, anchorTaskId, onPay }: GateFeeBatchModalProps) {
  const [loading, setLoading] = useState(false)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [candidates, setCandidates] = useState<GateFeeCandidate[]>([])
  const [maxPerBatch, setMaxPerBatch] = useState(50)
  const [truncated, setTruncated] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)

  // Drag-to-select. Holding the mouse down and sweeping the rows applies ONE
  // decision (tick or untick, whichever the first row needed) to everything it
  // passes over — dragging must never toggle each row independently, or a sweep
  // back over your own path silently undoes it.
  const drag = useRef<{ active: boolean; turnOn: boolean }>({ active: false, turnOn: true })

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !paying) onClose()
  }, [paying, onClose])

  useEffect(() => {
    if (!open) return
    document.addEventListener('keydown', handleKeyDown)
    const stop = () => { drag.current.active = false }
    document.addEventListener('mouseup', stop)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mouseup', stop)
    }
  }, [open, handleKeyDown])

  useEffect(() => {
    if (!open || !anchorTaskId) return
    let cancelled = false
    setLoading(true); setError(null); setCandidates([]); setSelected(new Set()); setSort(null)

    gateFeeApi.getCandidates(anchorTaskId)
      .then((res) => {
        if (cancelled) return
        setCandidates(res.candidates)
        setMaxPerBatch(res.maxPerBatch)
        setTruncated(res.truncated)
        // Only the row they clicked. Selecting everything by default would turn
        // one stray click into a few dozen containers' worth of money.
        const anchor = res.candidates.find(c => c.taskId === res.anchorTaskId && !c.blockedReason)
        setSelected(anchor ? new Set([anchor.taskId]) : new Set())
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load') })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [open, anchorTaskId])

  const sorted = useMemo(() => {
    if (!sort) return candidates
    const value = (c: GateFeeCandidate) =>
      (sort.key === 'terminal' ? c.terminalCode
        : sort.key === 'client' ? c.clientName
        : c.tradeType) || ''
    // Stable, case-insensitive, and blanks always last — a column of empty cells
    // bunched at the top tells the operator nothing.
    return [...candidates].sort((a, b) => {
      const av = value(a); const bv = value(b)
      if (!av && !bv) return 0
      if (!av) return 1
      if (!bv) return -1
      const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' })
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [candidates, sort])

  const toggleSort = (key: SortKey) => {
    setSort(prev => prev?.key === key
      ? (prev.dir === 'asc' ? { key, dir: 'desc' } : null)
      : { key, dir: 'asc' })
  }

  const selectable = candidates.filter(c => !c.blockedReason)
  const chosen = candidates.filter(c => selected.has(c.taskId))
  const directions = new Set(chosen.map(c => c.tradeType))
  const mixedDirection = directions.size > 1

  const apply = (taskId: number, on: boolean) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (on) { if (next.size < maxPerBatch) next.add(taskId) } else next.delete(taskId)
      return next
    })
  }

  const startDrag = (c: GateFeeCandidate) => {
    if (c.blockedReason || paying) return
    const turnOn = !selected.has(c.taskId)
    drag.current = { active: true, turnOn }
    apply(c.taskId, turnOn)
  }

  const dragOver = (c: GateFeeCandidate) => {
    if (!drag.current.active || c.blockedReason || paying) return
    apply(c.taskId, drag.current.turnOn)
  }

  const toggleAll = () => {
    setSelected(prev =>
      prev.size === selectable.length
        ? new Set()
        : new Set(selectable.slice(0, maxPerBatch).map(c => c.taskId)))
  }

  const submit = async () => {
    if (chosen.length === 0 || mixedDirection) return
    setPaying(true)
    try {
      const ok = await onPay(chosen.map(c => c.taskId))
      if (ok) onClose()
    } finally {
      setPaying(false)
    }
  }

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-green-600" />
            <h3 className="text-base font-semibold text-gray-900">Pay Gate Fees</h3>
          </div>
          <button type="button" onClick={onClose} disabled={paying}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-40" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 text-xs text-gray-500 border-b flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            The ticked containers go into one eModal fee search and one cart.{' '}
            <strong className="text-gray-700">You still complete the purchase yourself</strong>;
            EAloom files the receipt per container afterwards. Up to {maxPerBatch} at a time.
            Drag down the rows to tick several at once.
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 px-5 py-6 text-sm text-red-600">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
          </div>
        ) : candidates.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-500">
            No container is waiting on a gate fee right now.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto" onMouseLeave={() => { drag.current.active = false }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-5 py-2 text-left w-10">
                    <input type="checkbox" aria-label="Select all"
                      checked={selectable.length > 0 && selected.size === selectable.length}
                      onChange={toggleAll} disabled={paying} />
                  </th>
                  <th className="px-2 py-2 text-left">Container</th>
                  <th className="px-2 py-2 text-left">Task</th>
                  <SortHeader label="Terminal" k="terminal" sort={sort} onToggle={toggleSort} />
                  <SortHeader label="Client" k="client" sort={sort} onToggle={toggleSort} />
                  <SortHeader label="Trade" k="trade" sort={sort} onToggle={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const blocked = !!c.blockedReason
                  return (
                    <tr key={c.taskId}
                      onMouseDown={() => startDrag(c)}
                      onMouseEnter={() => dragOver(c)}
                      className={`border-t select-none ${
                        blocked ? 'bg-gray-50 text-gray-400'
                          : `cursor-pointer ${selected.has(c.taskId) ? 'bg-green-50' : 'hover:bg-green-50/40'}`}`}>
                      <td className="px-5 py-2">
                        <input type="checkbox" checked={selected.has(c.taskId)} disabled={blocked || paying}
                          onChange={() => apply(c.taskId, !selected.has(c.taskId))}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${c.containerNumber}`} />
                      </td>
                      <td className="px-2 py-2 font-medium">
                        {c.containerNumber}
                        {blocked && (
                          <span className="ml-2 text-[11px] text-amber-600">{c.blockedReason}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-gray-500">{c.taskNumber}</td>
                      <td className="px-2 py-2 text-gray-500">{c.terminalCode || '—'}</td>
                      <td className="px-2 py-2 text-gray-500">{c.clientName || '—'}</td>
                      <td className="px-2 py-2 text-gray-500">{c.tradeType}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {truncated && (
              <div className="px-5 py-2 text-xs text-amber-600">
                More than {maxPerBatch} containers owe a gate fee — only the first {maxPerBatch} are
                listed. Pay the rest in a second go.
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t flex items-center justify-between">
          <div className="text-xs">
            {mixedDirection ? (
              <span className="text-red-600">
                Import and export cannot be looked up together — eModal asks for one direction per
                search. Pay them in two goes.
              </span>
            ) : (
              <span className="text-gray-500" data-testid="gate-fee-selected-count">{chosen.length} selected</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={paying}>Cancel</Button>
            <Button size="sm" onClick={submit}
              disabled={chosen.length === 0 || mixedDirection || paying}
              className="bg-green-600 hover:bg-green-700 text-white">
              {paying ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Opening eModal…</>
                      : `Pay ${chosen.length} on eModal`}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default GateFeeBatchModal
